import { useCallback, useEffect, useMemo, useState } from "react";
import type { Client, Conversation } from "@twilio/conversations";
import { ConversationDto, ConversationState } from "../../dto/conversation";
import { mockGetConversationBySid } from "../../mocks/twilio/mockGetConversationBySid";
import { conversationsMap } from "../../conversations-objects";
import { USE_MOCKS } from "../../utils/localUtils";
import { http } from "../../utils/http";

export type TwilioConversationState = {
  sdkConvo?: Conversation;
  isLoading: boolean;
  hasLoadError: boolean;
  lastError?: { code?: number; message: string };
  reload: () => void;

  isErrorModalOpen: boolean;
  errorData?: { code: number; message: string };
  closeErrorModal: () => void;
};

export const useTwilioConversation = (
  client: Client | undefined,
  convoDto: ConversationDto | undefined,
  onForbidden?: (twilioSid: string) => void
): TwilioConversationState => {
  const [sdkConvo, setSdkConvo] = useState<Conversation | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [lastError, setLastError] = useState<
    { code?: number; message: string } | undefined
  >();

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorData, setErrorData] = useState<
    { code: number; message: string } | undefined
  >();

  const getBySid = useMemo(() => {
    if (USE_MOCKS) return (sid: string) => mockGetConversationBySid(sid);
    return (sid: string) => client!.getConversationBySid(sid);
  }, [client]);

  const loadConversation = useCallback(async () => {
    if ((!USE_MOCKS && !client) || !convoDto?.twilioSid) {
      setSdkConvo(undefined);
      setHasLoadError(false);
      setLastError(undefined);
      setIsErrorModalOpen(false);
      setErrorData(undefined);
      return;
    }

    setIsLoading(true);
    setHasLoadError(false);
    setLastError(undefined);
    setIsErrorModalOpen(false);
    setErrorData(undefined);
    const isOutOfDate =
      (!!convoDto.lastMessageAt &&
        Date.now() - new Date(convoDto.lastMessageAt).getTime() >
          24 * 60 * 60 * 1000) ||
      convoDto?.state === ConversationState.Inactive;

    try {
      if (isOutOfDate) {
        const res = await http.post(
          `/api/conversations/${convoDto.id}/assign/24h`,
          null
        );
        if (res.status !== 202) {
          throw new Error("[Forbidden] Unable to assign conversation.");
        }
      }

      const conv = await getBySid(convoDto.twilioSid);
      console.log("conv: ", conv.sid);
      conversationsMap.set(conv.sid, conv);
      setSdkConvo(conv);
    } catch (e: any) {
      const isForbidden =
        e?.message?.includes("Forbidden") || e?.status === 403;

      if (isForbidden && !isOutOfDate) {
        const res = await http.post(
          `/api/conversations/${convoDto.id}/assign/24h`,
          null
        );
        if (res.status !== 202) {
          throw new Error("[Forbidden] Unable to assign conversation.");
        }

        loadConversation();
        return;
      }

      console.error("[Twilio] Errore getConversationBySid:", e);

      const code = e?.status ?? 0;
      const message = e?.message ?? "Error";

      setSdkConvo(undefined);
      setHasLoadError(true);
      setLastError({ code, message });

      setIsErrorModalOpen(true);
      setErrorData({ code, message });
    } finally {
      setIsLoading(false);
    }
  }, [client, convoDto?.twilioSid, convoDto?.state, getBySid]);

  useEffect(() => {
    void loadConversation();
  }, [loadConversation]);

  const closeErrorModal = () => setIsErrorModalOpen(false);

  return {
    sdkConvo,
    isLoading,
    hasLoadError,
    lastError,
    reload: loadConversation,
    isErrorModalOpen,
    errorData,
    closeErrorModal,
  };
};
